import type { UserTask, UserTaskLabel } from "@/types/userTasks.ts";
import { GROUPS } from "./userTaskGrouping.ts";

/** A status group, as `byStatus` returns it. */
export type TaskGroup = { key: string; label: string; tasks: UserTask[] };

/**
 * What a card's mark says its task is.
 *
 * The filter's own words wherever one filter means one kind, so what the user
 * narrowed to and what each card claims to be are the same phrase. The two
 * filters that gather several kinds keep words of their own here, because no
 * single card is both — a snoozed task cannot wear "Closed / Snoozed" — and
 * the marks drop the "tasks" a filter needs in order to name a set.
 */
export const GROUP_MARK: Record<string, string> = {
  suggested: "Suggested",
  needsYou: "Needs your input",
  okthx: "Okthx is on it",
  waiting: "Waiting",
  later: "Snoozed",
  closed: "Closed",
};

/** What a card names itself by: the group's key, and the word for it. */
export type TaskIndicator = { key: string; label: string };

/**
 * What a single card says it is: the kind of task, not who it waits on.
 *
 * Derived from the task rather than the group it landed in, because a task
 * okthx holds while chasing someone sits in the okthx group, whose label would
 * never mention that at all. The key travels with the word: a row prints the
 * word, a card tints its mark and picks a glyph by the key, and neither can
 * be read back out of the other.
 */
export const taskIndicatorParts = (task: UserTask): TaskIndicator =>
  INDICATOR_BY_GROUP.get(GROUPS.find((group) => group.has(task))?.key ?? "") ?? NO_INDICATOR;

/**
 * One object per group, made once. A row is memoised on its props, and a
 * fresh object per call failed that comparison on every refetch, redrawing
 * every row for a list that had not changed.
 */
const INDICATOR_BY_GROUP = new Map<string, TaskIndicator>(
  GROUPS.map((group) => [group.key, { key: group.key, label: GROUP_MARK[group.key] ?? group.key }]),
);
const NO_INDICATOR: TaskIndicator = { key: "", label: "" };

/**
 * One way of narrowing the list, and the status groups it gathers.
 *
 * `groups: null` marks a filter that cuts across them rather than picking from
 * them — every task, or every task in a project. That is also what decides
 * whether its cards still have to say which kind they are.
 */
export type TaskFilter = { key: string; label: string; groups: string[] | null };

export const OPEN_FILTER = "open";
export const ALL_FILTER = "all";
export const PROJECTS_FILTER = "projects";
export const CLOSED_FILTER = "closed";

/**
 * Where the list opens.
 *
 * Open rather than All: what has been closed or put down is the record rather
 * than the work, and leading with it asks the user to read past it to reach
 * what is live. All is still a tap away, at the bottom where it is gone
 * looking for rather than landed on.
 */
export const DEFAULT_FILTER = OPEN_FILTER;

const ALL_TASKS: TaskFilter = { key: ALL_FILTER, label: "All tasks", groups: null };

/**
 * The filters that pick among the status groups, in the order they are offered.
 *
 * Open gathers several, and it and Closed between them are the one question a
 * list is first read for — is this still live?
 *
 * Waiting holds both kinds of parked task: one waiting on a person, and one
 * waiting on a date the reader set. A task they have committed to is not
 * finished and does not belong with the closed ones: a promise filed under
 * Closed is a promise nobody can see.
 *
 * Both are live, so both are in Open. A task waiting on Priya is no more
 * actionable today than one waiting on Thursday, and Open has always held the
 * first; keeping the second out would have drawn a line the reader cannot
 * see, between two kinds of parked that look the same to them.
 */
const STATUS_FILTERS: TaskFilter[] = [
  {
    key: OPEN_FILTER,
    label: "Open tasks",
    groups: ["suggested", "needsYou", "okthx", "waiting", "later"],
  },
  { key: "suggested", label: "Suggested tasks", groups: ["suggested"] },
  { key: "needsYou", label: "Needs your input", groups: ["needsYou"] },
  { key: "okthx", label: "Okthx is on it", groups: ["okthx"] },
  // Named in the order the two sort in, so the chip and the list below it do
  // not disagree about which comes first.
  { key: "waiting", label: "Waiting / Snoozed", groups: ["waiting", "later"] },
  { key: CLOSED_FILTER, label: "Closed", groups: ["closed"] },
];

/**
 * The one filter that gathers every task in a project, whichever project.
 *
 * One entry rather than one per project. A project is a different kind of
 * answer from a status, not several more of them, and spelled out the list
 * grows with the account until proper nouns are most of the menu. The count
 * says how much it gathers without naming any of it, and no project at all
 * hides the entry rather than offering "Projects (0)".
 */
export const projectsFilterLabel = (count: number) => `Projects (${count})`;

/**
 * The filters worth offering, in order.
 *
 * One is left out when nothing it gathers is on screen, for the reason an
 * empty status group is: a filter leading nowhere is a dead end the user has
 * to back out of.
 *
 * {@link ALWAYS_OFFERED} is the exception, All tasks among them. Those three
 * are the shape of the list rather than a slice of it, and a row that loses
 * one as the last task in it is answered rearranges itself under the reader
 * mid-answer, at the moment they are answering fastest and looking least. An
 * empty Open is also worth arriving at: it is the state the whole screen is
 * for, and a filter that hides itself the moment it is reached can never
 * say so.
 *
 * Projects and All sit last because they cut across the groups above them
 * rather than picking one of them.
 */
export const taskFilters = (groups: TaskGroup[], projectCount: number): TaskFilter[] => {
  const filled = new Set(groups.filter((group) => group.tasks.length > 0).map((g) => g.key));
  return [
    ...STATUS_FILTERS.filter(
      (f) => ALWAYS_OFFERED.has(f.key) || f.groups?.some((key) => filled.has(key)),
    ),
    ...(projectCount > 0
      ? [{ key: PROJECTS_FILTER, label: projectsFilterLabel(projectCount), groups: null }]
      : []),
    ALL_TASKS,
  ];
};

/**
 * Every filter there is, for before the list has loaded.
 *
 * Nothing is known to hold anything yet, so there is nothing to prune by, and
 * pruning anyway offers All alone — which then reads as the user's choice.
 * Projects is left out: its label counts something not yet counted.
 */
export const allTaskFilters = (): TaskFilter[] => [...STATUS_FILTERS, ALL_TASKS];

/**
 * The status filters offered whether or not they hold anything.
 *
 * Open and Closed are the two halves of the list; All, appended separately,
 * is the way back to both. Between them they are the frame every other filter
 * is a slice of, so they do not come and go with their contents.
 */
const ALWAYS_OFFERED = new Set<string>([OPEN_FILTER, CLOSED_FILTER]);

/**
 * The filter in effect: the one chosen, or what to fall back to when the data
 * has moved out from under it — the last task of a group answered, say.
 */
export const chosenFilter = (offered: TaskFilter[], key: string): TaskFilter =>
  offered.find((filter) => filter.key === key) ??
  offered.find((filter) => filter.key === DEFAULT_FILTER) ??
  ALL_TASKS;

/**
 * The projects the tasks on screen are spread across.
 *
 * A label is a project once it has a goal; the rest are plain labels, and not a
 * way anyone thinks about their list. One holding none of the tasks on screen
 * is left out, so it cannot count towards a filter that leads nowhere.
 *
 * "On screen" means a task some group shows, not every task the server sent.
 * A dismissed one is kept as the record of an answer and belongs to no group,
 * so counting its labels would offer a project that selects nothing — the
 * filter would stand, chosen, over an empty list.
 */
export const shownProjectIds = (
  labels: UserTaskLabel[] | undefined,
  tasks: UserTask[],
): Set<string> => {
  const held = new Set(
    tasks
      .filter((task) => GROUPS.some((group) => group.has(task)))
      .flatMap((task) => (task.labels ?? []).map((label) => label.id)),
  );
  return new Set(
    (labels ?? [])
      .filter((label) => label.goal !== null && held.has(label.id))
      .map((label) => label.id),
  );
};

/** Whether a task sits in any of them. */
export const inAnyProject = (task: UserTask, projectIds: Set<string>) =>
  (task.labels ?? []).some((label) => projectIds.has(label.id));

/** What a filter leaves on screen, from the groups that hold anything. */
export const narrowToFilter = (
  filter: TaskFilter,
  groups: TaskGroup[],
  projectIds: Set<string>,
): TaskGroup[] => {
  // Projects gather whatever they hold, whatever state each task is in, so
  // this narrows the tasks inside every group rather than picking groups.
  if (filter.key === PROJECTS_FILTER) {
    return groups
      .map((group) => ({
        ...group,
        tasks: group.tasks.filter((task) => inAnyProject(task, projectIds)),
      }))
      .filter((group) => group.tasks.length > 0);
  }
  if (filter.groups === null) return groups;
  const keys = new Set(filter.groups);
  return groups.filter((group) => keys.has(group.key));
};
